import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Plus, Search, Edit2, Trash2, ArrowLeft, RefreshCw, 
  Download, Upload, X, FileText, Clock, CheckCircle, Users, Phone, 
  Mail, MapPin, Briefcase, ArrowRight, Eye, PlayCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';


const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    team_size: '',
    contact_person: '',
    email: '',
    phone: '',
    address: ''
  });

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/customer-organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data.organizations || []);
      setPermissions(response.data.permissions || {});
      setSelectedOrgs([]); // Clear selection on refresh
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      industry: '',
      team_size: '',
      contact_person: '',
      email: '',
      phone: '',
      address: ''
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (org) => {
    setFormData({
      name: org.name || '',
      industry: org.industry || '',
      team_size: org.team_size || '',
      contact_person: org.contact_person || '',
      email: org.email || '',
      phone: org.phone || '',
      address: org.address || ''
    });
    setSelectedOrg(org);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleViewDetails = async (org) => {
    try {
      const response = await axios.get(`${API_URL}/api/customer-organizations/${org.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrg(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load organization details');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEditing && selectedOrg) {
        await axios.put(
          `${API_URL}/api/customer-organizations/${selectedOrg.org_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Organization updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/customer-organizations`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Organization created successfully');
      }
      setShowModal(false);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOrg) return;

    try {
      // Check if it's from assessment or customer database
      if (selectedOrg.source === 'assessment') {
        // Delete from assessments (uses admin endpoint)
        await axios.delete(
          `${API_URL}/api/admin/assessment/${selectedOrg.org_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Delete from customer organizations
        await axios.delete(
          `${API_URL}/api/customer-organizations/${selectedOrg.org_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Organization deleted successfully');
      setShowDeleteModal(false);
      setSelectedOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    }
  };

  // Bulk delete handlers
  const handleSelectOrg = (orgId, checked) => {
    if (checked) {
      setSelectedOrgs(prev => [...prev, orgId]);
    } else {
      setSelectedOrgs(prev => prev.filter(id => id !== orgId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select ALL deletable orgs (both customer_organizations and assessments for super_admin/org_admin)
      const deletableOrgs = filteredOrganizations
        .filter(org => permissions.delete)
        .map(org => org.org_id);
      setSelectedOrgs(deletableOrgs);
    } else {
      setSelectedOrgs([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrgs.length === 0) return;
    
    setDeleting(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      // Delete each selected organization using the appropriate endpoint
      for (const orgId of selectedOrgs) {
        const org = organizations.find(o => o.org_id === orgId);
        try {
          if (org?.source === 'assessment') {
            // Delete from assessments
            await axios.delete(`${API_URL}/api/admin/assessment/${orgId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Delete from customer organizations
            await axios.delete(`${API_URL}/api/customer-organizations/${orgId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to delete org ${orgId}:`, err);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} organization(s)`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} organization(s)`);
      }
      
      setSelectedOrgs([]);
      setShowBulkDeleteModal(false);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organizations');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customer-organizations/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { csv_content, filename } = response.data;
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Export completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export organizations');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const downloadSampleTemplate = () => {
    const sampleData = [
      ['Name', 'Industry', 'Team Size', 'Contact Person', 'Email', 'Phone', 'Address'],
      ['Acme Corp', 'Technology', '50-100', 'John Doe', 'john@acme.com', '+1234567890', '123 Main St'],
      ['Global Inc', 'Finance', '100-500', 'Jane Smith', 'jane@global.com', '+0987654321', '456 Oak Ave']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Organizations');
    XLSX.writeFile(wb, 'organizations_import_template.xlsx');
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          // Map Excel columns to organization fields
          const organizations = jsonData.map(row => ({
            name: row['Name'] || row['name'] || '',
            industry: row['Industry'] || row['industry'] || '',
            team_size: row['Team Size'] || row['team_size'] || '',
            contact_person: row['Contact Person'] || row['contact_person'] || '',
            email: row['Email'] || row['email'] || '',
            phone: row['Phone'] || row['phone'] || '',
            address: row['Address'] || row['address'] || ''
          }));

          const response = await axios.post(
            `${API_URL}/api/customer-organizations/import`,
            { organizations },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          toast.success(`Import completed: ${response.data.imported} imported, ${response.data.skipped} skipped`);
          setShowImportModal(false);
          setImportFile(null);
          fetchOrganizations();
        } catch (error) {
          toast.error('Failed to process import file');
        } finally {
          setImporting(false);
        }
      };
      reader.readAsArrayBuffer(importFile);
    } catch (error) {
      toast.error('Failed to import organizations');
      setImporting(false);
    }
  };

  const handleStartAssessment = (org) => {
    // Navigate to organization form with pre-filled data
    navigate(`/organization?customerOrg=${org.org_id}&name=${encodeURIComponent(org.name)}&industry=${encodeURIComponent(org.industry || '')}`);
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h1 className="font-heading font-bold text-2xl text-slate-900">Organizations</h1>
                  <p className="text-sm text-slate-500">Manage your customer database</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Bulk Delete Button - shows when items selected */}
              {selectedOrgs.length > 0 && permissions.delete && (
                <Button 
                  variant="destructive" 
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedOrgs.length})
                </Button>
              )}
              <Button variant="outline" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              {permissions.export && (
                <Button variant="outline" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
              {permissions.import && (
                <Button variant="outline" onClick={() => setShowImportModal(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              )}
              {permissions.create && (
                <Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Organization
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <Building2 className="h-8 w-8 text-purple-600" />
            </div>
            <div className="font-heading font-bold text-3xl text-slate-900">{organizations.length}</div>
            <div className="font-mono text-xs uppercase tracking-wide text-slate-500">Total Organizations</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <div className="font-heading font-bold text-3xl text-slate-900">
              {organizations.reduce((sum, org) => sum + (org.assessment_count || 0), 0)}
            </div>
            <div className="font-mono text-xs uppercase tracking-wide text-slate-500">Total Assessments</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="font-heading font-bold text-3xl text-slate-900">
              {organizations.reduce((sum, org) => sum + (org.completed_assessments || 0), 0)}
            </div>
            <div className="font-mono text-xs uppercase tracking-wide text-slate-500">Completed</div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Organizations List */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          {filteredOrganizations.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                {searchTerm ? 'No organizations found' : 'No organizations yet'}
              </h3>
              <p className="text-slate-500 mb-6">
                {searchTerm ? 'Try a different search term' : 'Add your first customer organization to get started'}
              </p>
              {permissions.create && !searchTerm && (
                <Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Organization
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {permissions.delete && (
                      <th className="w-12 px-4 py-4">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                          checked={selectedOrgs.length > 0 && selectedOrgs.length === filteredOrganizations.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          title="Select all organizations"
                        />
                      </th>
                    )}
                    <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-wide text-slate-500">Organization</th>
                    <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-wide text-slate-500">Industry</th>
                    <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-wide text-slate-500">Contact</th>
                    <th className="text-center px-6 py-4 font-mono text-xs uppercase tracking-wide text-slate-500">Assessments</th>
                    <th className="text-right px-6 py-4 font-mono text-xs uppercase tracking-wide text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredOrganizations.map((org, index) => (
                    <motion.tr
                      key={org.org_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`hover:bg-slate-50 transition-colors ${selectedOrgs.includes(org.org_id) ? 'bg-purple-50' : ''}`}
                    >
                      {permissions.delete && (
                        <td className="w-12 px-4 py-4">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                            checked={selectedOrgs.includes(org.org_id)}
                            onChange={(e) => handleSelectOrg(org.org_id, e.target.checked)}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Building2 className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900 flex items-center gap-2">
                              {org.name}
                              {org.source === 'assessment' && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full">From Assessment</span>
                              )}
                            </div>
                            <div className="text-sm text-slate-500">{org.team_size || '-'} employees</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {org.industry || 'Not specified'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-slate-900">{org.contact_person || '-'}</div>
                          <div className="text-slate-500">{org.email || '-'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {org.assessment_count || 0} total
                          </span>
                          {org.completed_assessments > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {org.completed_assessments} done
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(org)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartAssessment(org)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Start Assessment"
                          >
                            <PlayCircle className="h-4 w-4" />
                          </Button>
                          {permissions.edit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(org)}
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          )}
                          {permissions.delete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedOrg(org);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-xl text-slate-900">
                  {isEditing ? 'Edit Organization' : 'Add New Organization'}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter organization name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="e.g., Technology, Finance"
                  />
                </div>
                <div>
                  <Label htmlFor="team_size">Team Size</Label>
                  <Input
                    id="team_size"
                    value={formData.team_size}
                    onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                    placeholder="e.g., 50-100"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Primary contact name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="font-heading font-semibold text-xl text-slate-900">Delete Organization</h2>
              </div>
              <p className="text-slate-600 mb-6">
                Are you sure you want to delete <strong>{selectedOrg.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-xl text-slate-900">Import Organizations</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowImportModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <Button variant="outline" className="w-full" onClick={downloadSampleTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Sample Template
              </Button>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600 mb-2">
                  {importFile ? importFile.name : 'Select Excel or CSV file'}
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Choose File
                </Button>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={!importFile || importing}>
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-xl">
                    <Building2 className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-slate-900">{selectedOrg.name}</h2>
                    <p className="text-sm text-slate-500">{selectedOrg.industry || 'No industry specified'}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-6">
              {/* Organization Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Team Size</div>
                      <div className="font-medium">{selectedOrg.team_size || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Email</div>
                      <div className="font-medium">{selectedOrg.email || '-'}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Phone</div>
                      <div className="font-medium">{selectedOrg.phone || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Address</div>
                      <div className="font-medium">{selectedOrg.address || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assessment History */}
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Assessment History</h3>
                {selectedOrg.assessment_history && selectedOrg.assessment_history.length > 0 ? (
                  <div className="space-y-3">
                    {selectedOrg.assessment_history.map((assessment, index) => (
                      <div
                        key={assessment.org_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${assessment.status === 'completed' ? 'bg-green-100' : 'bg-orange-100'}`}>
                            {assessment.status === 'completed' ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <Clock className="h-5 w-5 text-orange-600" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              Assessment #{index + 1}
                            </div>
                            <div className="text-sm text-slate-500">
                              {assessment.created_at ? new Date(assessment.created_at).toLocaleDateString() : '-'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className={`text-sm font-medium ${assessment.status === 'completed' ? 'text-green-600' : 'text-orange-600'}`}>
                              {assessment.progress}% Complete
                            </div>
                            <div className="text-xs text-slate-500">
                              {assessment.stages_completed}/4 stages
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowDetailModal(false);
                              navigate(`/assessment-summary/${assessment.org_id}`);
                            }}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No assessments yet</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setShowDetailModal(false);
                        handleStartAssessment(selectedOrg);
                      }}
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Start First Assessment
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDetailModal(false);
                  handleStartAssessment(selectedOrg);
                }}
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Start New Assessment
              </Button>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="font-heading font-semibold text-xl text-slate-900">Delete Organizations</h2>
              </div>
              <p className="text-slate-600 mb-4">
                Are you sure you want to delete <strong>{selectedOrgs.length}</strong> selected organization(s)? 
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-red-700">
                  ⚠️ This action cannot be undone. All organization data will be permanently removed.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleBulkDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : `Delete ${selectedOrgs.length} Organization(s)`}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Organizations;
